import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useI18n, useT } from "@/i18n/I18nProvider";
import { formatNumber } from "@/i18n/format";

/**
 * 가상 스크롤 테이블 (이슈 #15 완료 기준: Workload 수가 많아도 가상화 적용)
 * --------------------------------------------------------------------------
 * 라이브러리를 붙이기 전이라 최소 구현입니다. 고정 행 높이를 전제로 하고,
 * 보이는 구간 + 위아래 여유분만 렌더링합니다.
 *
 * 중요한 건 성능 수치가 아니라 **갱신 시 스크롤과 선택이 초기화되지 않는 것**입니다.
 * 그래서 스크롤 위치는 DOM(컨테이너 scrollTop)이 그대로 들고 있고,
 * 데이터가 바뀌어도 컨테이너를 다시 만들지 않습니다.
 */

/* 번역문 안의 숫자 자리를 요소로 바꿔 끼우기 위한 sentinel입니다. 사람이 읽는
   문자열에 나타날 수 없는 제어문자를 씁니다 — 번역문에 우연히 들어 있을 수 없습니다. */
const TOTAL_SLOT = "\u0000vtable-total\u0000";
const RENDERED_SLOT = "\u0000vtable-rendered\u0000";

/** 한 번역 문장을 유지하면서 숫자만 `span.num` 요소로 끼웁니다. */
function interpolateCounts(template: string, total: string, rendered: string): ReactNode[] {
  return template.split(new RegExp(`(${TOTAL_SLOT}|${RENDERED_SLOT})`)).map((part, i) => {
    if (part === TOTAL_SLOT) {
      return (
        <span key={i} className="num">
          {total}
        </span>
      );
    }
    if (part === RENDERED_SLOT) {
      return (
        <span key={i} className="num">
          {rendered}
        </span>
      );
    }
    return part;
  });
}

export function VirtualTable<T>({
  items,
  rowHeight = 32,
  height = 420,
  overscan = 8,
  columns,
  header,
  renderRow,
  getKey,
  getRowClassName,
  empty,
  contentWidth,
}: {
  items: T[];
  rowHeight?: number;
  height?: number;
  overscan?: number;
  /**
   * 컬럼 폭(CSS 값 배열). head와 body는 별개의 <table>이므로, 같은 colgroup을
   * 양쪽에 렌더해야 컬럼 경계가 일치합니다. 없으면 fixed 레이아웃이 균등 분배합니다.
   */
  columns?: string[];
  header: ReactNode;
  renderRow: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string;
  getRowClassName?: (item: T, index: number) => string | undefined;
  empty?: ReactNode;
  /** Exact pixel width shared by head/body. The outer scroller owns horizontal motion. */
  contentWidth?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const { t } = useT("common");
  const { locale } = useI18n();

  const onScroll = useCallback(() => {
    if (ref.current) setScrollTop(ref.current.scrollTop);
  }, []);

  /* 목록이 짧아져 현재 스크롤이 범위를 벗어나면 그때만 보정합니다. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const max = Math.max(0, items.length * rowHeight - el.clientHeight);
    if (el.scrollTop > max) el.scrollTop = max;
  }, [items.length, rowHeight]);

  if (items.length === 0 && empty) return <>{empty}</>;

  const total = items.length * rowHeight;
  /* 행이 적으면 뷰포트를 줄입니다. 빈 스크롤 영역은 목록이 더 있는 것처럼 보이게 합니다. */
  const viewport = Math.min(height, Math.max(rowHeight * 3, total));
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  /* Count intersecting rows from both viewport edges. With 420/32 a non-aligned
     offset can intersect 15 rows; + 8 rows on each side is at most 31. */
  const visibleEnd = Math.ceil((scrollTop + viewport) / rowHeight);
  const end = Math.min(items.length, visibleEnd + overscan);
  const slice = items.slice(start, end);
  /* 바닥글은 **표시 언어를 따릅니다**. 예전에는 `전체 … 행`과 `ko-KR` 자리 구분이
     컴포넌트에 박혀 있어서, 이 표를 쓰는 모든 화면(Namespace·Workload·Resources·Nodes)이
     English 모드에서도 한국어 바닥글을 보여 줬습니다. ko 문구는 그대로 두었으므로
     기본 모습은 바뀌지 않습니다. (#207)

     숫자는 **자기 요소**(`span.num`)로 남깁니다. 번역문을 한 문장으로 받아 치환하면
     숫자가 문장 안으로 녹아들어 `ds-data-table`의 숫자 표기가 사라지고, 숫자만 집어
     확인하는 기존 표 회귀 테스트(CRDCatalog 등)도 깨집니다. 그래서 문장은 한 번역
     단위로 유지하되 자리표시자만 요소로 바꿔 끼웁니다 — 어순은 언어가 정합니다. */
  const footer = interpolateCounts(
    t("common.table.footer", { total: TOTAL_SLOT, rendered: RENDERED_SLOT }),
    formatNumber(locale, items.length),
    formatNumber(locale, slice.length),
  );
  const colgroup = columns ? (
    <colgroup>
      {columns.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  ) : null;

  return (
    <div className="vtable">
      <div className="vtable__scroller">
        <div className="vtable__content" style={contentWidth ? { width: contentWidth, minWidth: "100%" } : undefined}>
          <table className="ds-data-table ds-data-table--compact vtable__head">
            {colgroup}
            <thead>{header}</thead>
          </table>
          <div className="vtable__viewport" ref={ref} onScroll={onScroll} style={{ height: viewport }} tabIndex={0}>
            <div style={{ height: total, position: "relative" }}>
              <table
                className="ds-data-table ds-data-table--compact vtable__body"
                style={{ position: "absolute", top: start * rowHeight, left: 0, right: 0 }}
              >
                {colgroup}
                <tbody>
                  {slice.map((item, i) => (
                    <tr
                      key={getKey(item, start + i)}
                      className={getRowClassName?.(item, start + i)}
                      data-virtual-row="true"
                      style={{ height: rowHeight }}
                    >
                      {renderRow(item, start + i)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="vtable__footer">{footer}</div>
    </div>
  );
}
